import { computed, Injectable, signal } from '@angular/core';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly title?: string;
  readonly message: string;
  readonly durationMs: number;
}

const DEFAULT_DURATION_MS: Record<ToastTone, number> = {
  info: 4_000,
  success: 4_000,
  warning: 6_000,
  error: 8_000,
};

/**
 * Tiny signal-based toast queue.
 *
 * Replaces the legacy `UiNotificationService` (uiNotification). Components
 * render the queue via `notifications.queue()` and call back into
 * `dismiss(id)` to remove a toast manually.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private nextId = 1;
  private readonly internal = signal<readonly Toast[]>([]);

  readonly queue = computed(() => this.internal());

  info(message: string, title?: string): number {
    return this.push('info', message, title);
  }

  success(message: string, title?: string): number {
    return this.push('success', message, title);
  }

  warning(message: string, title?: string): number {
    return this.push('warning', message, title);
  }

  error(message: string, title?: string): number {
    return this.push('error', message, title);
  }

  dismiss(id: number): void {
    this.internal.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  clear(): void {
    this.internal.set([]);
  }

  private push(tone: ToastTone, message: string, title?: string): number {
    const id = this.nextId++;
    const toast: Toast = {
      id,
      tone,
      title,
      message,
      durationMs: DEFAULT_DURATION_MS[tone],
    };
    this.internal.update((toasts) => [...toasts, toast]);

    if (toast.durationMs > 0 && typeof window !== 'undefined') {
      window.setTimeout(() => this.dismiss(id), toast.durationMs);
    }
    return id;
  }
}
